import React, { createContext, useContext } from 'react';

const PermisosContext = createContext({ permisos: null, permisosPorModulo: null });

export const usePermisos = () => {
    const { permisos, permisosPorModulo } = useContext(PermisosContext);

    // funcion: Nombre de la función (ej: "PLANES DE PAGO - GESTIÓN DE SOLICITUDES")
    // accion: Tipo de acceso (ej: "SELECT", "INSERT", "_SELECT") -> Se normaliza a mayúsculas y sin guión bajo si es necesario
    // modulo: Opcional, nombre del módulo (ej: "EJECUCIONES TRIBUTARIAS...")
    const tienePermiso = (funcion, accion = "SELECT", modulo = null) => {
        if (!permisos || typeof permisos !== 'object') return false;

        // Normalizar acción (ej: "_SELECT" -> "SELECT")
        let acc = String(accion).toUpperCase();
        if (acc.startsWith("_")) acc = acc.substring(1);

        // Estrategia de búsqueda:
        // 1. Si se especifica módulo, buscar directo ahí.
        // 2. Si no, buscar la función en TODOS los módulos disponibles.

        if (modulo) {
            if (permisos[modulo] && permisos[modulo][funcion]) {
                const p = permisos[modulo][funcion];
                return !!(p[acc] || p["_" + acc]);
            }
            return false;
        }

        // Búsqueda en todos los módulos
        for (const modKey in permisos) {
            const mod = permisos[modKey];
            if (mod && typeof mod === 'object') {
                if (mod[funcion]) {
                    const p = mod[funcion];
                    // Chequear con y sin guión bajo por compatibilidad
                    if (p[acc] === true || p[acc] === "true" || p[acc] === 1) return true;
                    if (p["_" + acc] === true || p["_" + acc] === "true" || p["_" + acc] === 1) return true;
                }
            }
        }

        return false;
    };

    /**
     * Obtiene permisos para un módulo por su ID numérico.
     * Retorna { ver, editar, insertar, eliminar } - todos boolean.
     * Si no hay permisos para ese módulo, retorna todo false.
     */
    const permisosModulo = (idModulo) => {
        if (!permisosPorModulo || !idModulo) return { ver: false, editar: false, insertar: false, eliminar: false };
        return permisosPorModulo[idModulo] || { ver: false, editar: false, insertar: false, eliminar: false };
    };

    /**
     * Dado un módulo origen y un array de áreas conjuntas, determina:
     * - puedeVerTab(idModulo): si el usuario puede ver el tab de ese módulo
     * - puedeEditarTab(idModulo): si el usuario puede editar en ese módulo
     * - puedeEditarCompartido: si puede editar tabs compartidos (usa permisos del módulo origen)
     */
    const permisosExpediente = (idModuloOrigen, areasConjuntas = []) => {
        const origenPerms = permisosModulo(idModuloOrigen);

        return {
            // Para tabs específicos de módulo
            puedeVerTab: (idModulo) => permisosModulo(idModulo).ver,
            puedeEditarTab: (idModulo) => permisosModulo(idModulo).editar,

            // Para tabs compartidos (Partes, Movimientos, Vinculados) → usa permisos del módulo origen
            puedeEditarCompartido: origenPerms.editar,
            puedeVerCompartido: origenPerms.ver,

            // Acceso directo al módulo origen
            origen: origenPerms,
        };
    };

    return { permisos, permisosPorModulo, tienePermiso, permisosModulo, permisosExpediente };
};

export const PermisosProvider = ({ permisos, permisosPorModulo, children }) => (
    <PermisosContext.Provider value={{ permisos: permisos || null, permisosPorModulo: permisosPorModulo || null }}>
        {children}
    </PermisosContext.Provider>
);
